/**
 * Terms of Service Screen - MOBILE ONLY
 * 
 * Legal terms.
 * Stacked flow - back button, no bottom tabs.
 */

import React, { useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { color, text, space, touch, icon, layout } from '../../../shared/tokens/mobile';
import { ScreenContainer } from '../../../components/v2/mobile';

export default function TermsScreen() {
  const router = useRouter();

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  return (
    <ScreenContainer>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* HEADER */}
        <View style={styles.header}>
          <Pressable onPress={handleBack} style={styles.backButton}>
            <Ionicons name="chevron-back" size={icon.lg} color={color.textPrimary} />
          </Pressable>
          <Text style={styles.title}>Terms of Service</Text>
          <View style={styles.spacer} />
        </View>

        {/* CONTENT */}
        <View style={styles.content}>
          <Text style={styles.lastUpdated}>Last updated: December 2024</Text>

          <Text style={styles.sectionTitle}>1. Acceptance of Terms</Text>
          <Text style={styles.paragraph}>
            By accessing and using Farmer John ("the App"), you accept and agree to be bound by the terms and provisions of this agreement.
          </Text>

          <Text style={styles.sectionTitle}>2. Description of Service</Text>
          <Text style={styles.paragraph}>
            Farmer John provides property management and due diligence tracking tools for land investors and property managers. The service includes portfolio management, property tracking, and collaboration features.
          </Text>

          <Text style={styles.sectionTitle}>3. User Accounts</Text>
          <Text style={styles.paragraph}>
            You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
          </Text>

          <Text style={styles.sectionTitle}>4. Data and Privacy</Text>
          <Text style={styles.paragraph}>
            Your use of the App is also governed by our Privacy Policy. Please review our Privacy Policy to understand our practices.
          </Text>

          <Text style={styles.sectionTitle}>5. Acceptable Use</Text>
          <Text style={styles.paragraph}>
            You agree not to use the App for any unlawful purpose or in any way that could damage, disable, or impair the service.
          </Text>

          <Text style={styles.sectionTitle}>6. Intellectual Property</Text>
          <Text style={styles.paragraph}>
            The App and its original content, features, and functionality are owned by Pacaya Group and are protected by international copyright, trademark, and other intellectual property laws.
          </Text>

          <Text style={styles.sectionTitle}>7. Limitation of Liability</Text>
          <Text style={styles.paragraph}>
            The App is provided "as is" without warranties of any kind. We shall not be liable for any indirect, incidental, special, or consequential damages.
          </Text>

          <Text style={styles.sectionTitle}>8. Changes to Terms</Text>
          <Text style={styles.paragraph}>
            We reserve the right to modify these terms at any time. Continued use of the App after changes constitutes acceptance of the new terms.
          </Text>

          <Text style={styles.sectionTitle}>9. Contact</Text>
          <Text style={styles.paragraph}>
            If you have questions about these Terms, please contact us at legal@farmerjohn.app.
          </Text>
        </View>
      </ScrollView>
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: layout.scrollBottomPadding,
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: space.sm,
    paddingVertical: space.sm,
  },
  backButton: {
    width: touch.minimum,
    height: touch.minimum,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    flex: 1,
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
    textAlign: 'center',
  },
  spacer: {
    width: touch.minimum,
  },

  // Content
  content: {
    paddingHorizontal: space.md,
    paddingTop: space.md,
  },
  lastUpdated: {
    fontSize: text.caption.fontSize,
    fontWeight: text.caption.fontWeight,
    color: color.textMuted,
    marginBottom: space.lg,
  },
  sectionTitle: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
    marginTop: space.lg,
    marginBottom: space.sm,
  },
  paragraph: {
    fontSize: text.body.fontSize,
    fontWeight: text.body.fontWeight,
    lineHeight: text.body.lineHeight + 4,
    color: color.textSecondary,
  },
});

